<template>
  <Teleport to="body">
    <!-- Expanded: the filmstrip in the Client Pack modal frame -->
    <div v-if="job && job.panelOpen" class="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closePanel" />

      <div class="relative rounded-xl shadow-(--nviya-shadow-lg) overflow-hidden flex flex-col bg-white dark:bg-surface w-full"
           style="max-width: 720px; max-height: calc(100vh - 2rem);">
        <div class="flex items-center justify-between px-6 py-4 border-b shrink-0 border-(--nviya-border) dark:border-primary/30">
          <div>
            <h2 class="typo-section-title text-slate-900 dark:text-white">Client Pack</h2>
            <p class="text-xs mt-0.5 text-slate-500 dark:text-secondary">{{ job.companyName || ' ' }}</p>
          </div>
          <div class="flex items-center gap-2">
            <button v-if="!ended" type="button" @click="closePanel"
              class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-medium transition-colors border-(--nviya-border-strong) text-(--nviya-text-primary) hover:bg-(--nviya-hover)">
              <FontAwesome :icon="['fas', 'minus']" class="w-3 h-3" />
              Minimise
            </button>
            <button type="button" @click="closePanel" aria-label="Close"
              class="w-8 h-8 flex items-center justify-center rounded-lg transition-colors text-slate-400 hover:text-slate-700 hover:bg-(--nviya-figure-bg) dark:text-secondary dark:hover:text-white dark:hover:bg-primary/30">
              <FontAwesome :icon="['fas', 'xmark']" class="w-4 h-4" />
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <ClientPackFilmstrip :progress="job.progress" />
        </div>

        <div class="flex items-center justify-between gap-3 px-6 py-4 border-t border-(--nviya-border) dark:border-primary/30">
          <p class="text-xs text-(--nviya-text-muted)">
            <template v-if="!ended">You can close this. We will let you know when the pack is ready.</template>
            <template v-else-if="status === 'completed'">Ready to download.</template>
            <template v-else>The build did not finish.</template>
          </p>
          <button v-if="status === 'completed'" type="button" @click="download" :disabled="downloading"
            class="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-colors bg-(--nviya-blue) text-(--nviya-on-blue) hover:bg-(--nviya-blue-hover) disabled:opacity-60">
            <FontAwesome :icon="['fas', downloading ? 'circle-notch' : 'download']" :class="['w-3.5 h-3.5', downloading ? 'animate-spin' : '']" />
            Download
          </button>
          <button v-else-if="status === 'failed'" type="button" @click="retry(job.sessionId)"
            class="inline-flex items-center h-9 px-4 rounded-lg border text-sm font-semibold transition-colors border-(--nviya-border-strong) text-(--nviya-text-primary) hover:bg-(--nviya-hover)">
            Try again
          </button>
        </div>
      </div>
    </div>

    <!-- Minimised: a pill bottom-right, on every page, while the build runs -->
    <button v-else-if="job && !ended" type="button" @click="openPanel"
      class="cpp-pill fixed right-4 bottom-20 md:bottom-4 z-[70] flex items-center gap-2.5 py-2 pl-2.5 pr-3 rounded-full border bg-(--nviya-surface-raised) border-(--nviya-border) text-(--nviya-text-primary) hover:border-(--nviya-border-strong) transition-colors"
      :aria-label="`Client pack building, ${landed} of ${total} slides. Open progress.`">
      <span class="cpp-glyph flex items-end gap-0.5 w-[26px] h-[18px] p-[3px] rounded-[3px] border-[1.5px] border-(--nviya-accent)">
        <span class="flex-1 h-1/2 rounded-[1px] bg-(--nviya-accent)"></span>
        <span class="flex-1 h-[90%] rounded-[1px] bg-(--nviya-accent)"></span>
        <span class="flex-1 h-[65%] rounded-[1px] bg-(--nviya-accent)"></span>
      </span>
      <span class="flex flex-col items-start gap-1">
        <span class="text-xs font-semibold leading-[14px] whitespace-nowrap">
          Client pack<template v-if="job.companyName"> · {{ job.companyName }}</template>
          <span class="font-medium text-(--nviya-text-muted)"> {{ landed }}/{{ total }}</span>
        </span>
        <span class="flex gap-[3px]">
          <span v-for="s in dots" :key="s.id" :class="['w-1.5 h-1.5 rounded-[2px]', dotClass(s.state)]"></span>
        </span>
      </span>
    </button>
  </Teleport>
</template>

<script setup>
/**
 * Global Client Pack progress: the filmstrip panel and its minimised pill.
 * Mounted once in the dashboard layout; all state lives in useClientPackJob,
 * so the wizard, the pill and a page reload all show the same build.
 */
import { computed, onMounted, ref } from 'vue'
import ClientPackFilmstrip from '@/components/pitch-pack/ClientPackFilmstrip.vue'
import { useClientPackJob } from '@/composables/useClientPackJob'

const { activeJob, resume, retry, openPanel, closePanel, triggerDownload } = useClientPackJob()

const job = computed(() => activeJob.value)
const status = computed(() => job.value?.progress?.status)
const ended = computed(() => status.value === 'completed' || status.value === 'failed')
const total = computed(() => job.value?.progress?.total || 13)
const landed = computed(() => {
  const p = job.value?.progress
  return p ? p.built + p.suppressed + p.failed : 0
})
const dots = computed(() => job.value?.progress?.slides
  || Array.from({ length: total.value }, (_, i) => ({ id: `p${i}`, state: 'planned' })))

const dotClass = (state) => ({
  built: 'bg-(--nviya-success)',
  building: 'bg-(--nviya-accent) animate-pulse',
  suppressed: 'bg-(--nviya-border-strong)',
  failed: 'bg-(--nviya-critical)',
}[state] || 'bg-(--nviya-figure-bg)')

const downloading = ref(false)
const download = async () => {
  downloading.value = true
  try {
    await triggerDownload(job.value.sessionId)
  } finally {
    downloading.value = false
  }
}

onMounted(() => { resume() })
</script>

<style scoped>
/* A floating pill sits above the page, so it keeps a shadow — but the token,
   not a hand-rolled rgba. (It was 0 8px 24px -8px rgba(15,30,61,.30).) */
.cpp-pill { box-shadow: var(--nviya-shadow-md); }
</style>
